import { apiClient as api } from './api';
import { callV3, type ApiResponse } from '../api/envelope';
import type {
  AddAssetsToCollectionRequest,
  CollectionDetailResponse,
  CollectionDto,
  CreateCollectionRequest,
  ReorderCollectionAssetsRequest,
  UpdateCollectionRequest,
} from '../domain';

const COLLECTIONS_BASE = '/api/v3/collections';

export interface ListCollectionsParams {
  labId?: string;
  studioId?: string;
  page?: number;
  pageSize?: number;
}

/**
 * Path C Collections API (Slice 3). User-scoped via the `x-user-id` header set
 * in api.ts; cross-user access returns 404. All calls go through callV3 so the
 * envelope is unwrapped to the bare DTO (or thrown as ApiResponseError).
 */
export const CollectionService = {
  list: ({ labId, studioId, page = 1, pageSize = 50 }: ListCollectionsParams = {}) =>
    callV3<CollectionDto[]>(
      api.get<ApiResponse<CollectionDto[]>>(COLLECTIONS_BASE, {
        params: { labId, studioId, page, pageSize },
      }),
    ),

  get: (id: string) =>
    callV3<CollectionDetailResponse>(
      api.get<ApiResponse<CollectionDetailResponse>>(`${COLLECTIONS_BASE}/${id}`),
    ),

  create: (body: CreateCollectionRequest) =>
    callV3<CollectionDto>(
      api.post<ApiResponse<CollectionDto>>(COLLECTIONS_BASE, body),
    ),

  update: (id: string, body: UpdateCollectionRequest) =>
    callV3<CollectionDto>(
      api.patch<ApiResponse<CollectionDto>>(`${COLLECTIONS_BASE}/${id}`, body),
    ),

  delete: async (id: string): Promise<void> => {
    await callV3<unknown>(
      api.delete<ApiResponse<unknown>>(`${COLLECTIONS_BASE}/${id}`),
    );
  },

  addAssets: (id: string, body: AddAssetsToCollectionRequest) =>
    callV3<CollectionDto>(
      api.post<ApiResponse<CollectionDto>>(`${COLLECTIONS_BASE}/${id}/assets`, body),
    ),

  removeAsset: async (id: string, assetId: string): Promise<void> => {
    await callV3<unknown>(
      api.delete<ApiResponse<unknown>>(`${COLLECTIONS_BASE}/${id}/assets/${assetId}`),
    );
  },

  reorder: (id: string, assetIdsInOrder: string[]) =>
    callV3<CollectionDto>(
      api.put<ApiResponse<CollectionDto>>(`${COLLECTIONS_BASE}/${id}/assets/order`, {
        assetIdsInOrder,
      } as ReorderCollectionAssetsRequest),
    ),
};
